<script lang="ts">
  import { ChevronDown, Gift, MoveDiagonal, Settings } from "lucide-svelte";

  import { analytics } from "$lib/analytics";
  import { formatDollars } from "$lib/format";
  import useSWR from "$lib/swr";
  import Chip from "$lib/ui/system/Chip.svelte";
  import Dropdown from "$lib/ui/system/Dropdown.svelte";
  import DropdownItem from "$lib/ui/system/DropdownItem.svelte";
  import DropdownItemGroup from "$lib/ui/system/DropdownItemGroup.svelte";
  import { user } from "$lib/user";
  import UsageCreditsSummary from "./UsageCreditsSummary.svelte";
  import WorkspaceMetricsPopup from "./WorkspaceMetricsPopup.svelte";
  import {
    MemberRole,
    type WorkspaceActiveResources,
    type WorkspaceDescription,
  } from "~proto/modal_server/proto/public_api";

  export let workspace: string;

  const { data: currentWorkspace, update: updateUrl1 } =
    useSWR<WorkspaceDescription>();
  $: updateUrl1(`/api/workspaces/${workspace}`);

  const { data: resources, update: updateUrl2 } =
    useSWR<WorkspaceActiveResources>(undefined, { refreshInterval: 10000 });
  $: updateUrl2(`/api/workspaces/${workspace}/active-resources`);

  let workspaceMetricsPopupOpen = false;

  let isWorkspaceManager = false;
  $: if ($currentWorkspace && $user) {
    isWorkspaceManager =
      ($user.logged_in && $user.is_admin) ||
      $currentWorkspace.memberRole === MemberRole.MEMBER_ROLE_MANAGER ||
      $currentWorkspace.memberRole === MemberRole.MEMBER_ROLE_OWNER;
  }

  let remainingCredits = 0;
  $: if ($currentWorkspace) {
    remainingCredits = Math.max(
      $currentWorkspace.grantedCycleCredits - $currentWorkspace.cycleUsage,
      0,
    );
  }

  // Tracks if gpus and containers are within Workspace limits
  // status 0 = normal, 1 = warning, 2 = at workspace limit
  let gpuStatus = 0;
  let containerStatus = 0;
  $: if ($resources) {
    const remainingGpus = $resources.gpuLimit - $resources.activeGpus;
    const remainingGPUPercent = remainingGpus / $resources.gpuLimit;
    const remainingContainers =
      $resources.containerLimit - $resources.activeContainers;
    const remainingContainersPercent =
      remainingContainers / $resources.containerLimit;

    if (remainingGPUPercent === 0) {
      gpuStatus = 2;
    } else if (remainingGPUPercent <= 0.2) {
      gpuStatus = 1;
    } else {
      gpuStatus = 0;
    }

    if (remainingContainersPercent === 0) {
      containerStatus = 2;
    } else if (remainingContainersPercent <= 0.2) {
      containerStatus = 1;
    } else {
      containerStatus = 0;
    }
  }

  function variantByStatus(status: number) {
    if (status === 1) {
      return "warning";
    } else if (status === 2) {
      return "error";
    }

    return "default";
  }
</script>

{#if $user && $user.logged_in && $currentWorkspace}
  <Dropdown align="right">
    <button
      class="btn md:btn-outlined p-2 md:py-1.5 gap-x-2 md:bg-white/5"
      slot="button"
    >
      {#if remainingCredits}
        <Gift size={20} class="text-gray-500" />
      {:else}
        <div
          class="live-status md:ml-1 md:mr-0.5"
          class:live-status-running={$resources &&
            $resources.activeContainers > 0}
          class:live-status-warning={Math.max(gpuStatus, containerStatus) === 1}
          class:live-status-limit={Math.max(gpuStatus, containerStatus) === 2}
        ></div>
      {/if}
      <span class="hidden md:inline text-sm text-zinc-400 whitespace-nowrap">
        {#if remainingCredits}
          <span class="hidden lg:inline">Credits Remaining:</span>
          <span class="font-medium"
            >{formatDollars(remainingCredits, {
              showZeroCents: true,
            })}</span
          >
        {:else}
          <span class="hidden lg:inline">Live Usage:</span>
          <span class="font-medium"
            >{formatDollars($currentWorkspace.cycleUsage, {
              showZeroCents: true,
            })}</span
          >
        {/if}
      </span>
      <ChevronDown size={20} class="hidden md:block text-zinc-400" />
    </button>

    <div class="w-64"></div>

    <div class="text-sm m-2 px-2 py-1.5 space-y-2.5">
      <div>Billing period usage</div>
      <UsageCreditsSummary
        usage={$currentWorkspace.cycleUsage}
        credits={$currentWorkspace.grantedCycleCredits}
      />
    </div>

    {#if $resources}
      <hr />
      <div class="text-sm m-2 px-2 py-1.5">
        <div class="mb-2 flex justify-between items-center">
          <span>Active Resources</span>
          <button
            class="rounded-lg p-1 -m-1 hover:bg-white/5 transition-colors"
            on:click={() => {
              analytics.track("Workspace Aggregate Metrics Page Opened");
              workspaceMetricsPopupOpen = true;
            }}
          >
            <MoveDiagonal size={20} class="opacity-60" />
          </button>
        </div>
        <div class="mb-0.5 text-zinc-400">GPUs</div>
        <div class="flex gap-2 items-center mb-2">
          <span class="text-xl font-medium">{$resources.activeGpus}</span>
          <Chip variant={variantByStatus(gpuStatus)}
            >Limit: {$resources.gpuLimit}</Chip
          >
        </div>
        <div class="mb-0.5 text-zinc-400">Containers</div>
        <div class="flex gap-2 items-center">
          <div class="text-xl font-medium">
            {$resources.activeContainers}
          </div>
          <Chip variant={variantByStatus(containerStatus)}
            >Limit: {$resources.containerLimit}</Chip
          >
        </div>
      </div>
    {/if}

    {#if isWorkspaceManager}
      <hr />
      <DropdownItemGroup>
        <DropdownItem href="/settings/{workspace}/usage">
          <Settings size={20} class="text-zinc-400" slot="icon" />
          Usage & Billing Settings
        </DropdownItem>
      </DropdownItemGroup>
    {/if}
  </Dropdown>
{/if}

<WorkspaceMetricsPopup
  {workspace}
  open={workspaceMetricsPopupOpen}
  on:close={() => (workspaceMetricsPopupOpen = false)}
/>

<style lang="postcss">
  .live-status {
    @apply w-[8px] h-[8px] bg-white/30 rounded-full;
  }

  .live-status-running {
    @apply bg-primary;
    filter: drop-shadow(0px 0px 8px #aaf398);
  }

  .live-status-warning {
    @apply bg-warning;
    filter: drop-shadow(0px 0px 8px #fdc089);
  }

  .live-status-limit {
    @apply bg-error;
    filter: drop-shadow(0px 0px 8px #fd5d5d);
  }
</style>
